import { BUTTON_THEMES, Form, InputField, LoaderComponent } from 'ht-styleguide';
import { useForm, SubmitHandler } from 'react-hook-form';

/* Mutations */
import useUserMutationCreatePassword from '@store/queries/user/useUserMutation.createPassword';

/* Selectors / Hooks */
import { useUserRPTSelector } from '@store/queries/user/useUser.selector';
import useRouteController from '@scenes/HomeHub/hooks/hook.routeController';

/* Components */
import UserFlowHeader from '@scenes/HomeHub/pages/User/components/Header';
import { GridLayoutForm } from '@scenes/HomeHub/pages/User/components/GridLayouts';
import ButtonWrapper from '@scenes/HomeHub/pages/User/components/ButtonWrapper';
import UserFlowHeaderIcon from '@scenes/HomeHub/pages/User/components/ImageIcon';
import SubduedText from '@scenes/HomeHub/pages/User/components/SubduedText';
import Either from '@components/Either';

/* Integrations */
import { TRACKING_TYPES } from '@integrations/segment/homehub';

/* Constants / Utils */
import { YupSchemaResolver } from '@constants/validations';
import { TUserPageProps, UserScreenTypes } from '@scenes/HomeHub/pages/User/index';

import styles from './user.module.scss';

type TFormPassword = { password: string };

/**
 * New User Register Setup
 *
 * @returns {JSX.Element}
 * @constructor
 */
const UserNewPasswordRegisterStep = ({ userSegmentTracking }: TUserPageProps) => {
  /* Hooks */
  const rpt = useUserRPTSelector();
  const { surveyRoute, nextUserRoute } = useRouteController();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<TFormPassword>({ ...YupSchemaResolver(['password']) });

  /* Methods */
  /* Mutations */
  const { data, mutate, isPending } = useUserMutationCreatePassword();

  const registerFormAttributes: SubmitHandler<TFormPassword> = attributes => {
    mutate(
      { ...attributes, rpt },
      {
        onSuccess: (): any => {
          surveyRoute();
          userSegmentTracking(TRACKING_TYPES.finishedSurvey);
        },
        onError: () => nextUserRoute(UserScreenTypes.email),
      },
    );
  };

  return (
    <GridLayoutForm>
      <Form.Row classes="marginAuto width100">
        <Form.Column sm={4} md={6} mdOffset={1} lg={6} lgOffset={3}>
          <UserFlowHeaderIcon width="104" height="104" imageName="illus-lock" />
          <UserFlowHeader classes="paddingTop-small1" headerText="Protect your information and securely access your recommendations" />
          <SubduedText classes="p1 paddingTop-tiny1">Set a password to prevent others from accessing anything you share with us.</SubduedText>
        </Form.Column>
      </Form.Row>
      <Form.Row classes="marginAuto width100">
        <Form.Column sm={4} md={6} mdOffset={1} lg={4} lgOffset={4}>
          <Either condition={isPending || data?.user?.id}>
            <div className="paddingTop-small">
              <LoaderComponent />
            </div>

            <div className={styles.actionContainer}>
              <InputField
                type="password"
                placeholder="Enter a minimum of 6 characters"
                label=""
                error={errors.password?.message}
                {...register('password')}
              />
              <ButtonWrapper
                className="paddingTop-small1"
                disabled={!isDirty}
                onClick={handleSubmit(registerFormAttributes)}
                theme={BUTTON_THEMES.PRIMARY}
              >
                Set Password
              </ButtonWrapper>
            </div>
          </Either>
        </Form.Column>
      </Form.Row>
    </GridLayoutForm>
  );
};

export default UserNewPasswordRegisterStep;
